import React, { lazy, Suspense } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';

const CoreDashboardPage = lazy(() =>
  import('./modules/dashboard').then((m) => ({ default: m.CoreDashboardPage }))
);
const DirectoryPage = lazy(() =>
  import('./modules/directory').then((m) => ({ default: m.DirectoryPage }))
);
const MemberDetailPage = lazy(() =>
  import('./modules/directory').then((m) => ({ default: m.MemberDetailPage }))
);
const BatchesPage = lazy(() =>
  import('./modules/batches').then((m) => ({ default: m.BatchesPage }))
);
const BatchDetailPage = lazy(() =>
  import('./modules/batches').then((m) => ({ default: m.BatchDetailPage }))
);
const InquiriesPage = lazy(() =>
  import('./modules/inquiries').then((m) => ({ default: m.InquiriesPage }))
);
const CoreAttendancePage = lazy(() =>
  import('./modules/attendance').then((m) => ({ default: m.CoreAttendancePage }))
);
const StudyMaterialsPage = lazy(() =>
  import('./modules/study').then((m) => ({ default: m.StudyMaterialsPage }))
);
const FeeBookPage = lazy(() =>
  import('./modules/fee-book').then((m) => ({ default: m.FeeBookPage }))
);
const NoticeboardPage = lazy(() =>
  import('./modules/noticeboard').then((m) => ({ default: m.NoticeboardPage }))
);

import { RoutePermissionGuard } from '@shared/auth/RoutePermissionGuard';

const LoadingFallback: React.FC = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

export const coreRoutes: RouteObject[] = [
  {
    path: '',
    children: [
      {
        index: true,
        element: <Navigate to="dashboard" replace />,
      },
      {
        path: 'dashboard',
        handle: { crumb: 'Institute Cockpit' },
        element: (
          <RoutePermissionGuard permission="core.dashboard.read">
            <Suspense fallback={<LoadingFallback />}>
              <CoreDashboardPage />
            </Suspense>
          </RoutePermissionGuard>
        ),
      },
      {
        path: 'directory',
        handle: { crumb: 'Member Directory' },
        element: (
          <RoutePermissionGuard
            permission="core.directory.read"
            allowedRoles={['owner', 'admin', 'branch_head', 'teacher', 'counselor', 'accountant']}
          >
            <Suspense fallback={<LoadingFallback />}>
              <DirectoryPage />
            </Suspense>
          </RoutePermissionGuard>
        ),
      },
      {
        path: 'directory/:type/:id',
        handle: { crumb: 'Member 360' },
        element: (
          <RoutePermissionGuard
            permission="core.directory.read"
            allowedRoles={['owner', 'admin', 'branch_head', 'teacher', 'counselor', 'accountant']}
          >
            <Suspense fallback={<LoadingFallback />}>
              <MemberDetailPage />
            </Suspense>
          </RoutePermissionGuard>
        ),
      },
      {
        path: 'inquiries',
        handle: { crumb: 'Inquiries & Leads' },
        element: (
          <RoutePermissionGuard
            permission="core.inquiries.read"
            allowedRoles={['owner', 'admin', 'branch_head', 'counselor']}
          >
            <Suspense fallback={<LoadingFallback />}>
              <InquiriesPage />
            </Suspense>
          </RoutePermissionGuard>
        ),
      },
      {
        path: 'batches',
        handle: { crumb: 'Batches & Schedule' },
        element: (
          <RoutePermissionGuard permission="core.batches.read">
            <Suspense fallback={<LoadingFallback />}>
              <BatchesPage />
            </Suspense>
          </RoutePermissionGuard>
        ),
      },
      {
        path: 'batches/:batchId',
        handle: { crumb: 'Batch 360' },
        element: (
          <RoutePermissionGuard permission="core.batches.read">
            <Suspense fallback={<LoadingFallback />}>
              <BatchDetailPage />
            </Suspense>
          </RoutePermissionGuard>
        ),
      },
      {
        path: 'attendance',
        handle: { crumb: 'Batch Attendance' },
        element: (
          <RoutePermissionGuard permission="core.attendance.read">
            <Suspense fallback={<LoadingFallback />}>
              <CoreAttendancePage />
            </Suspense>
          </RoutePermissionGuard>
        ),
      },
      {
        path: 'study',
        handle: { crumb: 'Study Notes & Handouts' },
        element: (
          <RoutePermissionGuard permission="core.study.read">
            <Suspense fallback={<LoadingFallback />}>
              <StudyMaterialsPage />
            </Suspense>
          </RoutePermissionGuard>
        ),
      },
      {
        path: 'fee-book',
        handle: { crumb: 'Fee Record Book' },
        element: (
          <RoutePermissionGuard
            permission="core.feebook.read"
            allowedRoles={['owner', 'admin', 'branch_head', 'counselor', 'accountant', 'student', 'parent']}
          >
            <Suspense fallback={<LoadingFallback />}>
              <FeeBookPage />
            </Suspense>
          </RoutePermissionGuard>
        ),
      },
      {
        path: 'noticeboard',
        handle: { crumb: 'Noticeboard' },
        element: (
          <RoutePermissionGuard permission="core.notices.read">
            <Suspense fallback={<LoadingFallback />}>
              <NoticeboardPage />
            </Suspense>
          </RoutePermissionGuard>
        ),
      },
    ],
  },
];
